/* eslint sort-keys: 0 */
import { ClaimsActivityGhostProviderScenarioModel, ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityProviderScenario, ClaimsActivityProviderSummary, ClaimsActivityTopGhostProviderModel, PciDashboardTerminationDetails } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { ClaimsInsightsProviderScenarioResult, ClaimsInsightsProviderWidgetResult, ClaimsInsightsScoreSummaryModel, ClaimsInsightsSummaryScenarioModel } from "@app/projects/network-assessment/pci-dashboard/claims-insights";
import { PciDashboardDetailsModel, PciDashboardFilterData, PciDashboardFiltersPayload, PciDashboardGhostProviderCountsModel } from "@app/projects/network-assessment/pci-dashboard/shared";
import { ServiceBase } from "@app/shared";
import { PciDashboardProvider } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProvider";
import { ProviderNetworkType } from "@app/projects/enumerators";
import { PciDashboardProviderPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProviderPayload";

/** handles communication across the wire when interacting with the claims insights dashboard */
export default class PciDashboardService extends ServiceBase {
  private pciDashboardEndpoint = `${this.apiEndpoint}/pcidashboard`;

  public async getPciFilterData(projectId: number, runConfigurationId: number): Promise<PciDashboardFilterData> {
    if (!projectId || !runConfigurationId) {
      return null;
    }

    return await this.makeGet<PciDashboardFilterData>(`project/${projectId}/runconfiguration/${runConfigurationId}`, null, this.pciDashboardEndpoint);
  }

  public async getClaimsActivityProviderWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsActivityProviderSummary[]> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return [];
    }

    return await this.makePost<ClaimsActivityProviderSummary[]>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-activity-provider-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsActivityTopGhostCountyWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsActivityTopGhostProviderModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsActivityTopGhostProviderModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-activity-top-ghost-county-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsActivityTopGhostSpecialtyWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsActivityTopGhostProviderModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsActivityTopGhostProviderModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-activity-top-ghost-specialty-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsInsightsProviderWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsInsightsProviderWidgetResult[]> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return [];
    }

    return await this.makePost<ClaimsInsightsProviderWidgetResult[]>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-insights-provider-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsInsightsCountySummaryWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsInsightsScoreSummaryModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsInsightsScoreSummaryModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-insights-county-summary`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsInsightsSpecialtySummaryWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsInsightsScoreSummaryModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsInsightsScoreSummaryModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-insights-specialty-summary`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsActivityDashboardDetails(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<PciDashboardDetailsModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<PciDashboardDetailsModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-activity-dashboard-details`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsInsightsDashboardDetails(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<PciDashboardDetailsModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<PciDashboardDetailsModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-insights-dashboard-details`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getNetworkGhostProviderTerminationRecords(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<PciDashboardTerminationDetails[]> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<PciDashboardTerminationDetails[]>(`project/${projectId}/runconfiguration/${runConfigurationId}/get-ghost-provider-termination-record`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsActivityProviderScenarioWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsActivityProviderScenario[]> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsActivityProviderScenario[]>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-activity-provider-scenario-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsActivityGhostScenarioSpecialtyWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsActivityGhostProviderScenarioModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsActivityGhostProviderScenarioModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-activity-top-ghost-specialty-scenario-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsActivityGhostScenarioCountyWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsActivityGhostProviderScenarioModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsActivityGhostProviderScenarioModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-activity-top-ghost-county-scenario-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsInsightsProviderScenarioWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsInsightsProviderScenarioResult[]> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsInsightsProviderScenarioResult[]>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-insights-provider-scenario-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsInsightsScenarioCountySummaryWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsInsightsSummaryScenarioModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsInsightsSummaryScenarioModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-insights-county-summary-scenario-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getClaimsInsightsScenarioSpecialtySummaryWidgetData(
    projectId: number,
    runConfigurationId: number,
    filtersPayload: PciDashboardFiltersPayload
  ): Promise<ClaimsInsightsSummaryScenarioModel> {
    if (!projectId || !runConfigurationId || !filtersPayload) {
      return null;
    }

    return await this.makePost<ClaimsInsightsSummaryScenarioModel>(`project/${projectId}/runconfiguration/${runConfigurationId}/claims-insights-specialty-summary-scenario-widget`, filtersPayload, this.pciDashboardEndpoint);
  }

  public async getServicingProviderDetail(
    projectId: number,
    runConfigurationId: number,
    providerNetworkType: ProviderNetworkType,
    providerId: number,
    providerPayload: PciDashboardProviderPayload
  ): Promise<PciDashboardProvider> {
    if (!projectId || !runConfigurationId || !providerNetworkType || !providerId || !providerPayload) {
      return null;
    }
    return await this.makePost<PciDashboardProvider>(
      `pciDashboard/${projectId}/runconfiguration/${runConfigurationId}/provider/${providerId}/networktype/${providerNetworkType}`, providerPayload);
  }

  public async getGhostProviderCounts(
    projectIds: string[],
    lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum
  ): Promise<PciDashboardGhostProviderCountsModel> {

    return await this.makePost<PciDashboardGhostProviderCountsModel>(`pciDashboard/line-of-business/${lineOfBusiness}/get-ghost-counts`, projectIds);
  }

  public async restartPublishDashboardTable(
    projectId: number,
    runConfigurationId: number,
  ): Promise<boolean> {
    if (!projectId || !runConfigurationId) {
      return null;
    }

    return await this.makePut<boolean>(`project/${projectId}/runconfiguration/${runConfigurationId}/restart-publish`, null, this.pciDashboardEndpoint);
  }
}
